import React, { useEffect } from 'react';
import ImageCarousel from '../components/ImageCarousel';
import { natureImages } from '../mock';
import SEO from '../components/SEO';
import PageActions from '../components/PageActions';

const NaturePage = () => {
  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  const sections = [
    {
      id: 'dorset',
      title: 'Dorset',
      images: natureImages.dorset,
      description: 'Ancient landscapes shaped by time: rolling hills meet dramatic coastlines. The Jurassic Coast tells stories millions of years old. This is a place made for contemplation and wonder.'
    },
    {
      id: 'sea',
      title: 'The Sea',
      images: natureImages.sea,
      description: 'Close but unhurried. The coast\'s tides remind us of natural cycles; the horizon widens perspective and salt air clears the mind. A short journey brings you to where land meets water.'
    },
    {
      id: 'nature',
      title: 'Nature and Wildlife',
      images: natureImages.nature,
      description: 'You are a guest in nature\'s home. Ancient woodlands shelter deer and songbirds, wildflower meadows buzz with life, and every season repaints the countryside. Nature here is abundant, unhurried and alive.'
    },
    {
      id: 'walks',
      title: 'Walking and Trails',
      images: natureImages.walks,
      description: 'Step from your door onto paths that wind through field and forest. The trails start here at the farm. Whether you want an easy stroll or a day-long walk, the countryside unfolds at your pace.'
    },
    {
      id: 'night-sky',
      title: 'Night Sky and Silence',
      images: natureImages.nightSky,
      description: 'When darkness falls the stars emerge in rare brilliance. Away from city lights the Milky Way becomes visible and constellations reclaim the sky. Beneath it all is a silence so rare you can almost hear your own heartbeat.'
    }
  ];

  return (
    <div className="bg-[#f5f1e8]">
      <SEO 
        title="Your Landscape - Dorset Nature & Countryside | Your Setl"
        description="Immerse yourself in Dorset's natural beauty. Ancient woodlands, Jurassic Coast, walking trails, and infinite quietness. Experience nature as your companion on our 3-acre countryside retreat."
        keywords="Dorset countryside, Jurassic Coast, Dorset nature, walking trails Dorset, stargazing Dorset, UK countryside retreat, Dorset wildlife, coastal walks UK, rural Dorset, nature immersion"
        ogImage="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402352/DSC_8097_cedccb.jpg"
        url="https://www.yoursetl.co.uk/your-landscape"
      />
      <PageActions />
      {/* Hero Section */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${natureImages.hero})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 px-8 md:px-16 pb-16 md:pb-20 max-w-3xl">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#f5f1e8] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Your Landscape
          </h1>
          <div className="w-16 h-px bg-[#f5f1e8]/70 my-5" />
          <p
            className="text-base md:text-lg font-light text-[#f5f1e8] tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Not just a backdrop. Nature is your companion, your teacher, your path back to balance.
          </p>
        </div>
      </section>

      {/* Scrolling Sections */}
      {sections.map((section, index) => (
        <section
          key={section.id}
          id={section.id}
          className={`min-h-screen flex items-center py-20 px-6 ${
            index % 2 === 0 ? 'bg-[#f5f1e8]' : 'bg-[#e8e2d8]'
          }`}
        >
          <div className="max-w-7xl mx-auto w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div className={index % 2 === 0 ? 'order-1' : 'order-1 lg:order-2'}>
                <h2
                  className="text-4xl md:text-5xl font-light tracking-wide text-[#3a2f28] mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {section.title}
                </h2>
                <p
                  className="text-xl leading-relaxed text-[#3a2f28]/85"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {section.description}
                </p>
              </div>

              {/* Image Carousel */}
              <div className={index % 2 === 0 ? 'order-2' : 'order-2 lg:order-1'}>
                <div className="relative h-[400px] md:h-[550px] rounded-lg overflow-hidden shadow-2xl">
                  <ImageCarousel 
                    images={section.images} 
                    alt={section.title}
                    objectPosition={section.id === 'walks' ? ['right center', 'center', 'center', 'center'] : 'center'}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Closing Statement */}
      <section className="py-20 px-6 bg-[#3a2f28]">
        <div className="max-w-4xl mx-auto text-center">
          {/* Image */}
          <div className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden shadow-2xl mb-8">
            <img
              src={natureImages.infiniteQuietness[0]}
              alt="Infinite quietness"
              className="w-full h-full object-cover"
            />
          </div>
          
          <h2
            className="text-3xl md:text-5xl font-light tracking-wide text-[#f5f1e8] mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Infinite Quietness
          </h2>
          <p
            className="text-lg leading-relaxed text-[#f5f1e8]/90"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            In a world saturated with noise, we offer something increasingly rare: genuine silence. The kind that lets thoughts settle, creativity emerge, and perspective return. This is nature not as escape, but a homecoming.
          </p>
        </div>
      </section>
    </div>
  );
};

export default NaturePage;